import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, MessageCircle, User, Settings, LogOut, UserRoundPlus } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const NAV = [
  { to: "/", label: "Sobre" },
  { to: "/planos", label: "Planos" },
  { to: "/contato", label: "Contato" },
];

// CompraComigo é outra árvore de rotas (ver App.js): link de documento, não NavLink.
const COMPRACOMIGO = { href: "/compracomigo", label: "CompraComigo" };

function initialsFor(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const letters = parts.length > 1 ? [parts[0][0], parts[parts.length - 1][0]] : [parts[0][0]];
  return letters.join("").toUpperCase();
}

function ProfileMenu({ user, onLogout }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const incomplete = !user.profile_complete;

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={(o) => {
        if (o && incomplete) { setAlertOpen(true); return; }
        setMenuOpen(o);
      }}>
        <DropdownMenuTrigger asChild>
          <button
            className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
            data-testid="header-profile-trigger"
            aria-label="Menu do perfil">
            <Avatar className="h-10 w-10 border-2 border-[#f0edf7]">
              <AvatarImage src={user.photo_url} alt={user.full_name || "Perfil"} />
              <AvatarFallback className="mc-grad text-white text-sm font-semibold">
                {initialsFor(user.full_name)}
              </AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={10} className="w-56" data-testid="header-profile-menu">
          <DropdownMenuLabel className="font-semibold text-[#1e1b2e]">
            {user.full_name || "Minha conta"}
            <p className="text-xs font-normal text-muted-foreground truncate mt-0.5">{user.email}</p>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild data-testid="menu-meu-perfil">
            <Link to="/perfil" className="cursor-pointer"><User size={16} /> Meu Perfil</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild data-testid="menu-configuracoes">
            <Link to="/configuracoes" className="cursor-pointer"><Settings size={16} /> Configurações</Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onLogout} data-testid="menu-sair" className="cursor-pointer text-destructive focus:text-destructive">
            <LogOut size={16} /> Sair
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={alertOpen} onOpenChange={setAlertOpen}>
        <AlertDialogContent data-testid="incomplete-profile-alert">
          <AlertDialogHeader>
            <div className="mx-auto sm:mx-0 w-12 h-12 rounded-full mc-grad text-white flex items-center justify-center mb-1">
              <UserRoundPlus size={22} />
            </div>
            <AlertDialogTitle>Complete seu perfil</AlertDialogTitle>
            <AlertDialogDescription>
              Complete seu perfil para começar a usar o Mora Comigo — adicione uma foto, sua data de
              nascimento e seus interesses pra aparecer no Feed e ter matches mais compatíveis.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="incomplete-profile-later">Agora não</AlertDialogCancel>
            <AlertDialogAction className="btn-primary" data-testid="incomplete-profile-cta"
              onClick={() => navigate("/perfil")}>Completar perfil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const authed = user && user !== true;

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur border-b border-[#f0edf7]" data-testid="site-header">
      <div className="mc-container flex items-center justify-between h-[74px]">
        <Link to="/" data-testid="header-logo-link"><Logo /></Link>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[15px] font-semibold transition-colors ${isActive ? "text-violet-700" : "text-[#1e1b2e] hover:text-violet-600"}`}>
              {n.label}
            </NavLink>
          ))}
          <a href={COMPRACOMIGO.href} data-testid="nav-compracomigo"
            className="text-[15px] font-semibold transition-colors text-[#1e1b2e] hover:text-violet-600">
            {COMPRACOMIGO.label}
          </a>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {authed ? (
            <>
              <button className="btn-outline" data-testid="header-feed-btn" onClick={() => navigate("/feed")}>Feed</button>
              <Link to="/chat" data-testid="header-chat-link" aria-label="Chat"
                className="w-10 h-10 flex items-center justify-center rounded-full text-[#1e1b2e] hover:bg-[#f4f2fb] hover:text-violet-600 transition-colors">
                <MessageCircle size={22} />
              </Link>
              <ProfileMenu user={user} onLogout={doLogout} />
            </>
          ) : (
            <>
              <button className="btn-outline" data-testid="header-login-btn" onClick={() => navigate("/entrar")}>Login</button>
              <button className="btn-primary" data-testid="header-signup-btn" onClick={() => navigate("/cadastro")}>Criar conta</button>
            </>
          )}
        </div>

        <button className="md:hidden p-2 text-[#1e1b2e]" data-testid="header-menu-toggle" onClick={() => setOpen((o) => !o)} aria-label="Menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#f0edf7] bg-white px-5 py-4 flex flex-col gap-3" data-testid="mobile-menu">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
              className="text-[15px] font-semibold text-[#1e1b2e] py-1">{n.label}</NavLink>
          ))}
          <a href={COMPRACOMIGO.href} data-testid="mobile-nav-compracomigo"
            className="text-[15px] font-semibold text-[#1e1b2e] py-1">{COMPRACOMIGO.label}</a>
          {authed && (
            <>
              <NavLink to="/chat" onClick={() => setOpen(false)} className="text-[15px] font-semibold text-[#1e1b2e] py-1 flex items-center gap-2">
                <MessageCircle size={18} /> Chat
              </NavLink>
              <NavLink to="/perfil" onClick={() => setOpen(false)} className="text-[15px] font-semibold text-[#1e1b2e] py-1 flex items-center gap-2">
                <User size={18} /> Meu Perfil
              </NavLink>
              <NavLink to="/configuracoes" onClick={() => setOpen(false)} className="text-[15px] font-semibold text-[#1e1b2e] py-1 flex items-center gap-2">
                <Settings size={18} /> Configurações
              </NavLink>
            </>
          )}
          <div className="flex gap-3 pt-2">
            {authed ? (
              <>
                <button className="btn-outline flex-1" onClick={() => { setOpen(false); navigate("/feed"); }}>Feed</button>
                <button className="btn-primary flex-1" onClick={async () => { setOpen(false); await doLogout(); }}>Sair</button>
              </>
            ) : (
              <>
                <button className="btn-outline flex-1" onClick={() => { setOpen(false); navigate("/entrar"); }}>Login</button>
                <button className="btn-primary flex-1" onClick={() => { setOpen(false); navigate("/cadastro"); }}>Criar conta</button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
